/**
 * Analytics — per-campaign counters, the per-link click table, and the funnel.
 *
 * Reads exactly one endpoint, `GET /orgs/{org}/campaigns/{campaign}/report`
 * (reports:view), which already returns counters, rates and the click map. No
 * new backend: the Links tab and the KPI row are that payload rendered, and the
 * funnel is arithmetic on counters the report already carries.
 *
 * ONE denominator for engagement: `counters.delivered` (#369). The API's
 * `deliverabilityRates` divides opens and clicks by `delivered` (reporting.ts),
 * so every open, click and per-link CTR here uses the same base and agrees with
 * the Report screen and the series view. Delivered itself, and unsubscribes and
 * complaints, stay a share of `sent` — the base the halt gate uses. Each
 * percentage names its base on screen rather than leaving the reader to guess.
 *
 * `counters.opens` and `counters.clicks` are UNIQUE per subscriber (HotCounters
 * documents both, and `deriveCounters` builds them with a Set), so the "unique"
 * wording here is the data's own, not a label borrowed from the mock.
 *
 * The click-map overlay reads an authenticated decorated archive endpoint. Older
 * campaigns may still have only the Dynamo link map, so the explanatory fallback
 * remains intentional for those records.
 *
 * Arithmetic lives in exported pure helpers so it can be tested without a DOM
 * (`Analytics.test.tsx`), following `Report.tsx`'s export-for-test convention.
 */
import { useState } from "react";
import { useAsync } from "../useAsync.js";
import { CampaignPicker } from "../CampaignPicker.js";
import { Kpi } from "../Kpi.js";
import { can, type Grant } from "../rbac.js";
import { api, type AnalyticsTrends, type CampaignReport, type ClickMapRow } from "../api.js";
import { SkeletonTable } from "../Skeleton.js";
import { AutoOpensNote } from "../AutoOpensNote.js";

type Tab = "links" | "map" | "funnel";

/**
 * A percentage of `denom`, or `—` when the denominator is zero.
 *
 * 0/0 is not 0%: a campaign that delivered nothing has no open rate, and
 * printing "0.0%" invites an operator to read a send that never arrived as a
 * send nobody opened.
 */
export function pctOf(n: number, denom: number): string {
  if (denom <= 0) return "—";
  return `${((n / denom) * 100).toFixed(1)}%`;
}

/** Bar width in percent, clamped to [0,100] — never NaN, never off the card. */
export function barWidth(n: number, denom: number): number {
  if (denom <= 0) return 0;
  return Math.max(0, Math.min(100, (n / denom) * 100));
}

export interface FunnelRow {
  label: string;
  value: number;
  /** Share of the stage's base, as a percentage string, or `—` when the base is zero. */
  share: string;
  width: number;
}

/**
 * The engagement funnel: sent → delivered → unique opens → unique clicks.
 *
 * Delivered is a share of `sent`; opens and clicks are shares of `delivered`
 * (#369), matching the KPI row and the API's own rates. Bar widths stay on the
 * `sent` scale so the bars still narrow down the funnel. Opens and clicks are
 * unique-per-subscriber counts, so they are not strictly nested inside
 * `delivered` — the widths are computed independently rather than by assuming
 * monotonic nesting the data does not guarantee.
 */
export function funnelRows(c: CampaignReport["counters"]): FunnelRow[] {
  const sent = c.sent;
  const row = (label: string, value: number, base: number): FunnelRow => ({
    label,
    value,
    share: pctOf(value, base),
    width: barWidth(value, sent),
  });
  return [
    { label: "Sent", value: sent, share: sent > 0 ? "100.0%" : "—", width: sent > 0 ? 100 : 0 },
    row("Delivered", c.delivered, sent),
    row("Unique opens", c.opens, c.delivered),
    row("Unique clicks", c.clicks, c.delivered),
  ];
}

/**
 * `sent − delivered − bounces − rejects`: how much of the send has not yet
 * resolved either way.
 *
 * Deliberately NOT presented as the delivery-delay count. SES emits more than
 * one event per message — a message delayed and then delivered counts in both —
 * so this residual can go negative on real data, which is why it is clamped at
 * zero and labelled a derivation. The real count is `counters.deliveryDelays`
 * and it is rendered from the counter itself on the Links tab.
 */
export function unresolved(c: CampaignReport["counters"]): number {
  return Math.max(0, c.sent - c.delivered - c.bounces - c.rejects);
}

/** Per-link CTR is a share of `delivered`, the same base as the click rate (#369). */
export function linkCtr(row: ClickMapRow, delivered: number): string {
  return pctOf(row.clicks, delivered);
}

export function Analytics({ org, grant }: { org: string; grant: Grant | null }) {
  const campaigns = useAsync(() => api.campaigns(org), [org]);
  // Series parents never accumulate counters (each edition reports on its own),
  // so they are left out of the picker, as on the Campaign report (#348).
  const series = useAsync(() => api.series(org), [org]);
  const trends = useAsync(() => api.analyticsTrends(org, 30), [org]);
  const [campaign, setCampaign] = useState("");
  const [report, setReport] = useState<CampaignReport | null>(null);
  const [tab, setTab] = useState<Tab>("links");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [archiveHtml, setArchiveHtml] = useState<string | null>(null);

  const load = async (id: string = campaign) => {
    const campaign = id;
    setErr("");
    setReport(null);
    setArchiveHtml(null);
    setLoading(true);
    try {
      const next = await api.report(org, campaign);
      setReport(next);
      try {
        setArchiveHtml((await api.archive(org, campaign)).html);
      } catch {
        // Older sends may have the Dynamo archive/link map but predate the
        // generic S3 body. Keep the report useful and explain that state below.
        setArchiveHtml(null);
      }
    } catch (e) {
      setErr(String(e));
    } finally {
      setLoading(false);
    }
  };

  if (!can(grant, "reports:view", org)) {
    return (
      <div>
        <div className="pagehead"><div><h1>Analytics</h1><p>Campaign counters, deliverability rates and the per-link click table.</p></div></div>
        <p className="muted">Your role can't view reports.</p>
      </div>
    );
  }

  const c = report?.counters;
  const rows = report?.clickMap.rows ?? [];

  return (
    <div>
      <div className="pagehead"><div><h1>Analytics</h1><p>Campaign counters, deliverability rates and the per-link click table.</p></div></div>
      <p className="muted">
        Open and click percentages are
        of <b>delivered</b> emails; the delivered, unsubscribe and complaint percentages are of <b>sent</b>. Campaign opens
        and clicks are counted once per subscriber; per-link clicks count all recorded click events.
      </p>

      <CampaignPicker
        campaigns={(campaigns.data ?? []).filter((c) => !(series.data ?? []).some((x) => x.seriesId === c.campaignId))}
        loading={campaigns.loading}
        selected={campaign}
        onPick={(id) => { setCampaign(id); void load(id); }}
      />
      {loading && <p className="muted">Loading report…</p>}

      {campaigns.error && <p className="err">{campaigns.error}</p>}
      {err && <p className="err">{err}</p>}

      <TrendsPanel trends={trends.data} loading={trends.loading} error={trends.error} />

      {report && c && (
        <>
          <div className="card">
            {/* The shared tile, with the rate folded into the label — the same
                idiom `Report.tsx` uses, rather than a second KPI markup. */}
            <div className="kpis">
              <Kpi n={c.delivered} l={`delivered · ${pctOf(c.delivered, c.sent)} of sent`} />
              <Kpi n={c.opens} l={`unique opens · ${pctOf(c.opens, c.delivered)} of delivered`} />
              <Kpi n={c.clicks} l={`unique clicks · ${pctOf(c.clicks, c.delivered)} of delivered`} />
              <Kpi
                n={c.unsubscribes + c.complaints}
                l={`unsub / complaint · ${pctOf(c.unsubscribes + c.complaints, c.sent)} of sent`}
              />
            </div>
            <AutoOpensNote />
          </div>

          <div className="card row" role="tablist">
            <button
              className={tab === "links" ? "btn" : "btn ghost"}
              role="tab"
              aria-selected={tab === "links"}
              onClick={() => setTab("links")}
            >
              Links
            </button>
            <button
              className={tab === "map" ? "btn" : "btn ghost"}
              role="tab"
              aria-selected={tab === "map"}
              onClick={() => setTab("map")}
            >
              Click map
            </button>
            <button
              className={tab === "funnel" ? "btn" : "btn ghost"}
              role="tab"
              aria-selected={tab === "funnel"}
              onClick={() => setTab("funnel")}
            >
              Funnel
            </button>
          </div>

          {tab === "links" && <LinksTab report={report} />}
          {tab === "map" && <ClickMapTab rowCount={rows.length} archiveHtml={archiveHtml} />}
          {tab === "funnel" && <FunnelTab report={report} />}
        </>
      )}
    </div>
  );
}

export function TrendsPanel({ trends, loading, error }: { trends?: AnalyticsTrends | null; loading: boolean; error?: string }) {
  const points = trends?.points ?? [];
  const maxSent = Math.max(1, ...points.map((point) => point.sent));
  return (
    <div className="card">
      <div className="row" style={{ justifyContent: "space-between" }}>
        <div>
          <strong>30-day trends</strong>
          <div className="muted">Daily event totals across campaigns. Opens and clicks are unique subscribers per day; their percentages are of that day's delivered.</div>
        </div>
        {trends && <span className="muted">{trends.from} → {trends.through}</span>}
      </div>
      {loading && <SkeletonTable rows={4} label="Loading trends…" />}
      {error && <p className="muted">Trends unavailable: {error}</p>}
      {!loading && !error && trends && (
        <>
          {trends.summary && <TrendSummary summary={trends.summary} from={trends.from} through={trends.through} />}
          {points.every((point) => point.sent === 0 && point.delivered === 0 && point.opens === 0 && point.clicks === 0 && point.bounces === 0 && point.complaints === 0) ?
            <p className="muted">No events in this window.</p> :
            <table>
            <thead><tr><th>Date</th><th>Sent</th><th>Delivered</th><th>Opens</th><th>Clicks</th><th /></tr></thead>
            <tbody>
              {points.filter((point) => point.sent || point.delivered || point.opens || point.clicks || point.bounces || point.complaints).map((point) => (
                <tr key={point.date}>
                  <td>{point.date}</td>
                  <td>{point.sent}</td>
                  <td>{point.delivered}</td>
                  <td>{point.opens} <span className="muted">({pctOf(point.opens, point.delivered)})</span></td>
                  <td>{point.clicks} <span className="muted">({pctOf(point.clicks, point.delivered)})</span></td>
                  <td style={{ width: "24%" }}><div className="bar" style={{ width: `${barWidth(point.sent, maxSent)}%` }} /></td>
                </tr>
              ))}
            </tbody>
            </table>}
          {/* The panel is also the Dashboard's trend card, so this one note
              covers the open figures on both screens (#369). */}
          <AutoOpensNote />
        </>
      )}
    </div>
  );
}

function comparison(current: number, previous: number): string {
  if (previous <= 0) return "—";
  const delta = ((current - previous) / previous) * 100;
  return `${delta >= 0 ? "+" : ""}${delta.toFixed(1)}% vs prior period`;
}

/** A snapshot older than this means the nightly job missed at least one run. */
const STALE_SNAPSHOT_MS = 36 * 60 * 60 * 1000;

function formatWhen(iso: string): string {
  const d = new Date(iso);
  const local = d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  const utc = d.toISOString().slice(0, 16).replace("T", " ");
  return `${local} (${utc} UTC)`;
}

function TrendSummary({ summary, from, through, now = Date.now() }: {
  summary: NonNullable<AnalyticsTrends["summary"]>; from: string; through: string; now?: number;
}) {
  const audience = summary.audience ?? null;
  const c = summary.current;
  const stale = audience !== null && now - new Date(audience.computedAt).getTime() > STALE_SNAPSHOT_MS;
  return (
    <>
      <section style={{ margin: "14px 0" }} aria-label="Audience">
        <strong>Audience</strong>
        {audience ? (
          <>
            <div className="muted">
              Counted {formatWhen(audience.computedAt)}. These numbers come from a nightly count, run at 02:30 UTC
              right after we copy in Amazon SES's suppression list, so they can be up to a day old. Today's signups,
              unsubscribes and bounces show up after tonight's count.
            </div>
            {stale && (
              <p className="err">
                This count is more than a day old, so the nightly job has missed at least one run. The numbers below
                are from the date shown above.
              </p>
            )}
            <div className="kpis" style={{ margin: "10px 0" }}>
              <Kpi n={audience.mailable.toLocaleString()} l="mailable subscribers" />
              <Kpi n={audience.suppressed.toLocaleString()} l="suppressed subscribers" />
              <Kpi n={audience.subscriberRecords.toLocaleString()} l="subscriber records in total" />
            </div>
            <ul className="muted" style={{ margin: "0 0 10px", paddingLeft: 18 }}>
              <li><b>Mailable subscribers</b>: people confirmed on at least one newsletter whom we can send to. Each person is counted once, however many newsletters they get.</li>
              <li><b>Suppressed subscribers</b>: people we will not send to: they hard-bounced, marked mail as spam, are on Amazon SES's suppression list, or unsubscribed from everything.{audience.newlyMarkedSuppressed > 0 && ` Last night's count marked ${audience.newlyMarkedSuppressed.toLocaleString()} more records suppressed because SES had already blocked them.`}</li>
              <li><b>Subscriber records in total</b>: every address we hold, including people who never confirmed or have since unsubscribed.</li>
            </ul>
            {audience.lists.length > 0 && (
              <table>
                <thead><tr><th>Newsletter</th><th title="Subscriptions marked confirmed, before suppression is taken out">Confirmed</th><th>Mailable</th><th>Free</th><th>Paid</th></tr></thead>
                <tbody>
                  {audience.lists.map((l) => (
                    <tr key={l.listId}>
                      <td>{l.name}</td>
                      <td>{l.confirmed.toLocaleString()}</td>
                      <td>{l.mailable.toLocaleString()}</td>
                      <td>{l.free.toLocaleString()}</td>
                      <td>{l.paid.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {audience.lists.length > 0 && (
              <div className="muted" style={{ marginTop: 6 }}>
                <b>Confirmed</b> is everyone who confirmed that newsletter; <b>Mailable</b> leaves out those who are
                suppressed. The gap is people a send would skip. Per-newsletter figures add up to more than the
                mailable total because one person can get several newsletters.
              </div>
            )}
          </>
        ) : (
          <p className="muted">
            Audience counts have not been calculated yet. They are counted nightly at 02:30 UTC and will appear
            here after the first run.
          </p>
        )}
      </section>
      <section style={{ margin: "14px 0" }} aria-label="Engagement">
        <strong>Engagement, {from} → {through}</strong>
        <div className="muted">
          Calculated live from the email events each time this page loads, and compared with the same number of days
          just before.
        </div>
        <div className="kpis" style={{ margin: "10px 0" }}>
          <Kpi n={c.emailsSent.toLocaleString()} l={`emails sent · ${comparison(c.emailsSent, summary.previous.emailsSent)}`} />
          <Kpi n={`${(c.openRate * 100).toFixed(1)}%`} l={`average open rate · ${comparison(c.openRate, summary.previous.openRate)}`} />
          <Kpi n={`${(c.clickRate * 100).toFixed(1)}%`} l={`average click rate · ${comparison(c.clickRate, summary.previous.clickRate)}`} />
          {c.uniqueClickers !== undefined && (
            <Kpi
              n={c.uniqueClickers.toLocaleString()}
              l={`people clicked · ${audience ? `${pctOf(c.uniqueClickers, audience.mailable)} of mailable subscribers` : "share unavailable until the first audience count"} · ${comparison(c.uniqueClickers, summary.previous.uniqueClickers ?? 0)}`}
            />
          )}
        </div>
        <ul className="muted" style={{ margin: 0, paddingLeft: 18 }}>
          <li><b>Emails sent</b>: individual emails handed to Amazon SES in this period, one per recipient per send.</li>
          <li><b>Average open rate</b>: the average of each send's own open rate in this period. For each send, the different people who opened it, divided by how many of its emails were delivered. A send with nothing delivered in the period is left out. Automatic opens make this run high; see the note at the end of this card.</li>
          <li><b>Average click rate</b>: the same average for clicks: for each send, the different people who clicked, divided by how many of its emails were delivered.</li>
          <li><b>People clicked</b>: how many different people clicked at least one link in any email in this period. Each person counts once, however many links or emails they clicked. The share is out of today's mailable subscribers, so it shows how much of your reachable audience actually engaged. Someone who clicked and later unsubscribed still counts as a clicker but is no longer a subscriber.</li>
        </ul>
      </section>
    </>
  );
}

function LinksTab({ report }: { report: CampaignReport }) {
  const c = report.counters;
  const rows = report.clickMap.rows;
  // Widths are relative to the busiest link, not to `delivered` — a 3% CTR would
  // otherwise draw every bar as an invisible sliver.
  const maxClicks = Math.max(1, ...rows.map((r) => r.clicks));
  const maxEvent = Math.max(1, c.bounces, c.rejects, c.deliveryDelays, c.renderingFailures);

  return (
    <>
      <div className="card">
        <div className="muted" style={{ marginBottom: 8 }}>
          Recorded links from the campaign archive. Clicks count events; Unique counts subscribers
          per link. Clicks / delivered includes repeat clicks and can exceed 100%.
        </div>
        {rows.length === 0 ? (
          // An empty table would read as "zero clicks on every link". The
          // distinction that matters: no link map was recorded for this
          // campaign at all, which is not the same as nobody clicking.
          <p className="muted">
            No link map recorded for this campaign. The click table is built from the archived link map written at send
            time, so campaigns sent before that record existed — or sends with no trackable links — have nothing to show
            here.
          </p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Link</th>
                <th>Clicks</th>
                <th>Unique</th>
                <th>Clicks / delivered</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.linkId}>
                  <td title={r.urlTemplate}>{r.label}</td>
                  <td>{r.clicks}</td>
                  <td>{r.unique}</td>
                  <td>{linkCtr(r, c.delivered)}</td>
                  <td style={{ width: "30%" }}>
                    <div className="bar" style={{ width: `${barWidth(r.clicks, maxClicks)}%` }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card">
        <div className="muted" style={{ marginBottom: 8 }}>
          Every other delivery event — its own counter each
        </div>
        <table>
          <tbody>
            {(
              [
                ["Bounces", c.bounces],
                ["Rejects", c.rejects],
                ["Delivery delays", c.deliveryDelays],
                ["Rendering failures", c.renderingFailures],
              ] as const
            ).map(([label, n]) => (
              <tr key={label}>
                <td style={{ width: 150 }}>{label}</td>
                <td style={{ width: 70 }}>{n}</td>
                <td>
                  <div className="bar" style={{ width: `${barWidth(n, maxEvent)}%` }} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="muted">
          None of rejects, delivery delays or rendering failures suppresses anyone. A <b>reject</b> is SES accepting the
          message and then refusing to send it — nothing reached a receiver, so it is not a bounce. A{" "}
          <b>delivery delay</b> records a temporary delivery problem, such as a full mailbox or
          throttling; a later delivery may also be counted. A <b>rendering failure</b> records
          SES failing to render a template, for example because template data is missing.
        </p>
      </div>
    </>
  );
}

/**
 * The overlay tab. This is not a load failure and not a temporary empty state —
 * the archived body it would paint onto is never written, so the tab explains
 * the gap and points at the half that does ship.
 */
function ClickMapTab({ rowCount, archiveHtml }: { rowCount: number; archiveHtml: string | null }) {
  if (archiveHtml) {
    return <div className="card">
      <div className="muted" style={{ marginBottom: 8 }}>Click map · archived copy</div>
      <p className="muted">Click counts are attached to the links below. The preview is the generic campaign body; recipient-specific values and magic-link tokens are intentionally not shown.</p>
      <iframe title="Campaign click map" srcDoc={archiveHtml} style={{ width: "100%", minHeight: 520, border: "1px solid var(--border)", borderRadius: 8, background: "white" }} sandbox="" />
    </div>;
  }
  return (
    <div className="card">
      <div className="muted" style={{ marginBottom: 8 }}>
        Click map · archived copy
      </div>
      <p className="muted">
        <b>Archive preview unavailable for this campaign.</b> The click counts themselves are real and complete
        {rowCount > 0 ? ` — all ${rowCount} of them are on the Links tab.` : "; they appear on the Links tab once a campaign has a recorded link map."}
      </p>
      <p className="muted">
        New sends write a generic rendered body and the reporting service decorates it with click badges. This older
        campaign either predates that body or the archive read is temporarily unavailable, so the Links tab remains
        the authoritative click data.
      </p>
    </div>
  );
}

function FunnelTab({ report }: { report: CampaignReport }) {
  const c = report.counters;
  const rows = funnelRows(c);
  const stuck = unresolved(c);

  return (
    <div className="card">
      <div className="muted" style={{ marginBottom: 12 }}>
        Engagement funnel — delivered as a percentage of sent; opens and clicks as a percentage of delivered
      </div>
      <table>
        <tbody>
          {rows.map((r) => (
            <tr key={r.label}>
              <td style={{ width: 130 }}>{r.label}</td>
              <td style={{ width: 150 }}>
                {r.value} · {r.share}
              </td>
              <td>
                <div className="bar" style={{ width: `${r.width}%` }} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="muted">
        Unresolved estimate: <b>{stuck}</b> — derived as sent − delivered − bounces − rejects ({c.sent} − {c.delivered}{" "}
        − {c.bounces} − {c.rejects}), clamped at zero. This is a derivation, not a count: SES emits more than one event
        per message, so it is not the same figure as the <b>{c.deliveryDelays}</b> delivery delays counted on the Links
        tab.
      </p>
      <AutoOpensNote />
    </div>
  );
}
